/**
 * A serial, self-pacing queue for Google Places lookups.
 *
 * The PDF import screen resolves one place per extracted item. A 30-item
 * itinerary used to fan those out on 300ms timers — roughly 60 Places calls in
 * nine seconds, since each lookup is a predictions call followed by a details
 * call. Google answered OVER_QUERY_LIMIT, which the caller could not tell apart
 * from "no such place", so it reported a miss AND immediately fired a fallback
 * query — adding load at the exact moment Google asked for less. Nothing was
 * ever retried, so every place after the first few stayed unresolved.
 *
 * This queue runs one lookup at a time and lets Google set the pace: a throttled
 * lookup is retried with growing backoff rather than reported as a miss, and the
 * penalty eases once calls succeed again. A definitive answer is never retried —
 * no amount of waiting turns ZERO_RESULTS into a place.
 */

const THROTTLED = 'OVER_QUERY_LIMIT';

// Statuses that retrying cannot improve: the place is absent, or the request
// itself is wrong. Anything else is treated as transient.
const DEFINITIVE = ['OK', 'ZERO_RESULTS', 'REQUEST_DENIED', 'INVALID_REQUEST', 'NOT_FOUND'];

function createPlaceResolveQueue(options = {}) {
  const {
    // Spacing between lookups while nothing is wrong. Deliberately unhurried:
    // one lookup is two Google calls (predictions, then details), and being
    // throttled is far more expensive than going slowly -- a single
    // OVER_QUERY_LIMIT costs up to 8s of backoff here and can escalate to a
    // 15-30s retry round, which dwarfs the time this gap adds.
    minGapMs = 600,
    initialBackoffMs = 500,  // first penalty after a throttle
    maxBackoffMs = 8000,
    maxAttempts = 5,
    sleep = ms => new Promise(resolve => setTimeout(resolve, ms)),
  } = options;

  const waiting = [];
  let draining = false;
  let penaltyMs = 0;
  const stats = { completed: 0, throttled: 0, givenUp: 0 };

  function noteThrottled() {
    stats.throttled += 1;
    penaltyMs = Math.min(penaltyMs ? penaltyMs * 2 : initialBackoffMs, maxBackoffMs);
  }

  // Ease off gradually rather than dropping straight back to full speed, which
  // would just trip the limiter again on the next item.
  function noteSucceeded() {
    penaltyMs = penaltyMs > initialBackoffMs ? Math.floor(penaltyMs / 2) : 0;
  }

  async function attempt(task) {
    for (let tries = 1; tries <= maxAttempts; tries += 1) {
      const wait = Math.max(minGapMs, penaltyMs);
      if (wait > 0) await sleep(wait);

      let outcome;
      try {
        outcome = await task();
      } catch (e) {
        return { place: null, status: 'ERROR', error: e };
      }

      const status = (outcome && outcome.status) || 'ERROR';
      if (status === THROTTLED) {
        noteThrottled();
        continue;
      }
      if (DEFINITIVE.indexOf(status) !== -1) {
        noteSucceeded();
        return outcome;
      }
      // Unknown or transient status — back off and try again.
      noteThrottled();
    }

    stats.givenUp += 1;
    return { place: null, status: THROTTLED };
  }

  async function drain() {
    if (draining) return;
    draining = true;
    while (waiting.length) {
      const { task, resolve } = waiting.shift();
      const outcome = await attempt(task);
      stats.completed += 1;
      resolve(outcome);
    }
    draining = false;
  }

  return {
    /**
     * Queue one lookup. `task` returns a promise for `{ place, status }`.
     * Resolves with the final outcome — never rejects.
     */
    run(task) {
      return new Promise((resolve) => {
        waiting.push({ task, resolve });
        drain();
      });
    },
    stats() {
      return { ...stats, penaltyMs, queued: waiting.length };
    },
  };
}

module.exports = { createPlaceResolveQueue };
